import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Search, Loader2, FileText, Folder, AlertCircle, Sparkles } from 'lucide-react';
import { knowledgeService } from '@/services/knowledge.service';
import { useState, useEffect } from 'react';
import { AISearchAnswer } from '@/components/AISearchAnswer';

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [inputValue, setInputValue] = useState(query);

  // RAG Query for AI answer
  const {
    data: ragResponse,
    isLoading: ragLoading,
    error: ragError,
  } = useQuery({
    queryKey: ['rag-query', query],
    queryFn: () => knowledgeService.ragQuery(query, { maxSources: 5 }),
    enabled: !!query && query.length >= 3,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 1, // Only retry once on failure
  });

  // Keyword search for additional results
  const {
    data: searchResults,
    isLoading: searchLoading,
  } = useQuery({
    queryKey: ['search', query],
    queryFn: () => knowledgeService.search(query, { limit: 20 }),
    enabled: !!query,
  });

  // Feedback mutation
  const feedbackMutation = useMutation({
    mutationFn: (helpful: boolean) =>
      knowledgeService.submitRagFeedback({
        queryId: ragResponse?.metadata?.timestamp || query,
        helpful,
      }),
  });

  useEffect(() => {
    setInputValue(query);
  }, [query]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setSearchParams({ q: inputValue.trim() });
    }
  };

  const handleFeedback = (helpful: boolean) => {
    feedbackMutation.mutate(helpful);
  };

  const isLoading = ragLoading && searchLoading;
  const hasRagResponse = ragResponse && ragResponse.answer;
  const hasSearchResults = searchResults?.results && searchResults.results.length > 0;

  return (
    <div className="py-8">
      <div className="container max-w-4xl">
        {/* Search Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Search Results</h1>
          <form onSubmit={handleSearch} className="max-w-2xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Ask a question or search for articles..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </form>

          {/* AI Search indicator */}
          {query && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Sparkles className="h-4 w-4 text-purple-500" />
              <span>AI-powered search enabled</span>
            </div>
          )}
        </div>

        {/* Results */}
        {!query ? (
          <div className="text-center py-16">
            <Search className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-600 dark:text-gray-400">Enter a question to search our knowledge base</p>
          </div>
        ) : isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Searching and generating AI answer...</p>
          </div>
        ) : (
          <div>
            {/* AI Answer Section */}
            {ragLoading ? (
              <div className="bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 rounded-xl border border-purple-200 mb-6 p-6">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Generating AI answer...</span>
                </div>
              </div>
            ) : ragError ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl mb-6 p-4">
                <div className="flex items-center gap-2 text-yellow-700">
                  <AlertCircle className="h-5 w-5" />
                  <span className="text-sm">AI answer unavailable. Showing search results instead.</span>
                </div>
              </div>
            ) : hasRagResponse ? (
              <AISearchAnswer
                answer={ragResponse.answer}
                confidence={ragResponse.confidence}
                sources={ragResponse.sources}
                onFeedback={handleFeedback}
              />
            ) : null}

            {/* Search Results Section */}
            {searchLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
              </div>
            ) : hasSearchResults ? (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {hasRagResponse ? 'Related Articles' : 'Search Results'}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Found {searchResults.total} {searchResults.total === 1 ? 'result' : 'results'} for "{query}"
                </p>

                <div className="space-y-4">
                  {searchResults.results.map((result) => (
                    <Link
                      key={`${result.type}-${result.id}`}
                      to={result.type === 'article' ? `/article/${result.slug}` : `/category/${result.slug}`}
                      className="block bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:border-primary-300 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                          {result.type === 'article' ? (
                            <FileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                          ) : (
                            <Folder className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded capitalize">
                              {result.type}
                            </span>
                            {result.categoryName && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">{result.categoryName}</span>
                            )}
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white hover:text-primary-600 transition-colors">
                            {result.title}
                          </h3>
                          {result.excerpt && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{result.excerpt}</p>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {searchResults.totalPages > 1 && (
                  <div className="mt-8 flex justify-center gap-2">
                    {Array.from({ length: searchResults.totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setSearchParams({ q: query, page: page.toString() })}
                        className={`px-4 py-2 rounded-lg ${
                          page === searchResults.page
                            ? 'bg-primary-600 text-white'
                            : 'bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16">
                <Search className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No results found</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  We couldn't find any matches for "{query}". Try different keywords.
                </p>

                {searchResults?.suggestions && searchResults.suggestions.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Did you mean:</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {searchResults.suggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          onClick={() => setSearchParams({ q: suggestion })}
                          className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm hover:bg-primary-100 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
